import { reportDayjs } from '../../../helpers/reportRange';
import { EReportDataInterval } from '../types/report-log.type';

/**
 * Longest range (calendar days, inclusive) each interval may load. The same
 * limit applies to the report log, the PDF and the XLSX download. An interval
 * missing here (week, month) is turned off for now.
 */
export const REPORT_INTERVAL_MAX_DAYS: Partial<Record<string, number>> = {
  [EReportDataInterval.minute]: 3,
  [EReportDataInterval.hour]: 14,
  [EReportDataInterval.day]: 31,
};

const ENABLED_INTERVALS = [
  EReportDataInterval.minute,
  EReportDataInterval.hour,
  EReportDataInterval.day,
];

export const MAX_REPORT_RANGE_DAYS = Math.max(
  ...ENABLED_INTERVALS.map((interval) => REPORT_INTERVAL_MAX_DAYS[interval]!)
);

export const getReportRangeDaysInclusive = (
  startAt: number,
  endAt: number,
  timezone: number
) =>
  Math.max(
    0,
    reportDayjs(endAt, timezone)
      .startOf('day')
      .diff(reportDayjs(startAt, timezone).startOf('day'), 'day')
  ) + 1;

export const isReportIntervalEnabled = (interval?: string) =>
  !!interval && REPORT_INTERVAL_MAX_DAYS[interval] !== undefined;

export const isReportRangeAllowed = (
  interval: string | undefined,
  rangeDaysInclusive: number
) =>
  isReportIntervalEnabled(interval) &&
  rangeDaysInclusive <= REPORT_INTERVAL_MAX_DAYS[interval!]!;

/** Finest interval the range allows, day for anything longer. */
export const resolveReportInterval = (rangeDaysInclusive: number) =>
  ENABLED_INTERVALS.find((interval) =>
    isReportRangeAllowed(interval, rangeDaysInclusive)
  ) || EReportDataInterval.day;

/**
 * End of a range cut to maxDays from its start, keeping the time of the end.
 * The range is returned as is when it is short enough.
 */
export const clampReportRangeEnd = (
  startAt: number,
  endAt: number,
  timezone: number,
  maxDays: number
) => {
  if (getReportRangeDaysInclusive(startAt, endAt, timezone) <= maxDays) {
    return endAt;
  }

  const end = reportDayjs(endAt, timezone);

  return reportDayjs(startAt, timezone)
    .startOf('day')
    .add(maxDays - 1, 'day')
    .set('hour', end.hour())
    .set('minute', end.minute())
    .set('second', 0)
    .unix();
};

/** Why the filter can not be loaded or downloaded, undefined when it can. */
export const getReportRangeError = (filter: {
  interval?: string;
  startAt: number;
  endAt: number;
  timezone: number;
}) => {
  if (!isReportIntervalEnabled(filter.interval)) {
    return `Interval ${filter.interval || '-'} is temporarily disabled`;
  }

  if (
    !isReportRangeAllowed(
      filter.interval,
      getReportRangeDaysInclusive(filter.startAt, filter.endAt, filter.timezone)
    )
  ) {
    return `Range for interval ${filter.interval} is limited to ${
      REPORT_INTERVAL_MAX_DAYS[filter.interval!]
    } days`;
  }

  return undefined;
};
